import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";

const BRANDING_CACHE_KEY = "cantierepro-branding";

const leggiBrandingSalvato = () => {
  try {
    const salvato = window.localStorage.getItem(BRANDING_CACHE_KEY);
    return salvato ? JSON.parse(salvato) : null;
  } catch {
    return null;
  }
};

export default function AppShell({ children, wide = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(leggiBrandingSalvato);

  useEffect(() => {
    let attivo = true;

    api
      .get("/settings")
      .then((response) => {
        if (!attivo) return;

        const aggiornato = response.data || {};
        setBranding(aggiornato);

        try {
          window.localStorage.setItem(
            BRANDING_CACHE_KEY,
            JSON.stringify(aggiornato)
          );
        } catch {
          // L'app continua a funzionare anche se il browser blocca localStorage.
        }
      })
      .catch(() => {});

    return () => {
      attivo = false;
    };
  }, []);

  const tornaDashboard = () => {
    navigate("/");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            type="button"
            data-testid="home-logo"
            onClick={tornaDashboard}
            aria-label="Torna alla Dashboard"
            title="Torna alla Dashboard"
            className="flex items-center gap-3 group text-left"
          >
            {branding?.logo_path ? (
              <div className="h-10 w-12 rounded-lg border bg-white p-1 flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
                <img
                  src={`${API}/uploads/bolla/${branding.logo_path}`}
                  alt="Logo aziendale"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <div
                className="h-10 w-12 rounded-lg border border-transparent"
                aria-hidden="true"
              />
            )}

            {branding ? (
              <span className="font-heading font-extrabold text-lg tracking-tight">
                {branding.company_name || ""}
              </span>
            ) : (
              <span className="h-5 w-28 rounded bg-muted animate-pulse" aria-hidden="true" />
            )}
          </button>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold leading-none">{user?.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{user?.email}</p>
            </div>

            <Button
              variant="ghost"
              size="icon"
              data-testid="settings-link"
              onClick={() => navigate("/impostazioni")}
              className="rounded-md hover:bg-accent"
            >
              <Settings className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              data-testid="logout-button"
              onClick={logout}
              className="rounded-md hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className={`${wide ? "max-w-[1480px]" : "max-w-7xl"} mx-auto px-4 sm:px-6 py-8 sm:py-12`}>
        {children}
      </main>
    </div>
  );
}

